import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const Home = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [nextIndex, setNextIndex] = useState(1);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const images = [
    '/hero-1.jpeg',
    '/hero-2.jpeg',
    '/hero-3.jpeg',
    '/hero-4.jpeg',
    '/hero-5.jpeg'
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setIsTransitioning(true);

      // Start fading in the next image
      setNextIndex((currentIndex + 1) % images.length);

      // After the transition is complete, update the current index
      const timer = setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % images.length);
        setIsTransitioning(false);
      }, 1500); // Match this with the transition duration

      return () => clearTimeout(timer);
    }, 5000); // Total time between transitions (including fade duration)

    return () => clearInterval(interval);
  }, [currentIndex, images.length]);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-black/40 z-10"></div>
          <div className="absolute inset-0 w-full h-full">
            {/* Current Image */}
            <motion.div
              key={`current-${currentIndex}`}
              className="absolute inset-0 w-full h-full"
              initial={{ opacity: 1 }}
              animate={{ opacity: isTransitioning ? 0 : 1 }}
              transition={{ duration: 1.5, ease: 'easeInOut' }}
            >
              <img
                src={images[currentIndex]}
                alt={`Hero ${currentIndex + 1}`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = `https://via.placeholder.com/1920x1080/2b2b2b/d2691e?text=Hero+${currentIndex + 1}`;
                }}
              />
            </motion.div>

            {/* Next Image (only shown during transition) */}
            {isTransitioning && (
              <motion.div
                key={`next-${nextIndex}`}
                className="absolute inset-0 w-full h-full"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1.5, ease: 'easeInOut' }}
              >
                <img
                  src={images[nextIndex]}
                  alt={`Hero ${nextIndex + 1}`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = `https://via.placeholder.com/1920x1080/2b2b2b/d2691e?text=Hero+${nextIndex + 1}`;
                  }}
                />
              </motion.div>
            )}
          </div>
        </div>

        <div className="container mx-auto px-4 z-20 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Capturing Moments, <span className="text-burnt-orange">Telling Stories</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              We create stunning visual content that brings your vision to life. From documentaries to commercial films, we tell stories that resonate.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <motion.div
                whileHover={{
                  scale: 1.05,
                  backgroundColor: "#FF5722", // Subtle background change
                }}
                whileTap={{ scale: 0.95 }}
                transition={{
                  type: "spring",
                  stiffness: 400,
                  damping: 20,
                }}
              >
                <Link
                  to="/contact"
                  className="bg-burnt-orange text-white font-semibold py-3 px-8 rounded-md transition-all duration-300 ease-out"
                >
                  Get In Touch
                </Link>
              </motion.div>
              <motion.div
                whileHover={{
                  scale: 1.05,
                  backgroundColor: "rgba(255, 255, 255, 0.1)", // Subtle background highlight
                }}
                whileTap={{ scale: 0.95 }}
                transition={{
                  type: "spring",
                  stiffness: 400,
                  damping: 20,
                }}
              >
                <Link
                  to="/our-work"
                  className="bg-transparent text-white font-semibold py-3 px-8 border-2 border-white rounded-md transition-all duration-300 ease-out"
                >
                  View Our Work
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>

        <div className="absolute bottom-8 left-0 right-0 flex justify-center z-20">
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 2 }}
            className="text-white"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </motion.div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 bg-dark-gray">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Our Services</h2>
            <div className="w-20 h-1 bg-burnt-orange mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[{
              title: "Documentary Films",
              description: "At SAMFREN OJO MEDIA, our documentaries spotlight real lives, untold stories, and marginalized voices across Africa. We bring depth, emotion, and authenticity to every story — from personal journeys to social impact narratives — capturing the beauty and complexity of human experience.",
              icon: "🎥"
            }, {
              title: "Music Videos",
              description: "We create original music videos that match your sound with cinematic visuals. From the concept to execution, we bring your song to life with storytelling and crisp editing that feels global.",
              icon: "🎵"
            }, {
              title: "Commercial & Product Videos",
              description: "We produce commercial campaigns, product highlights, and promotional videos that captivate audiences and elevate brands. From script to screen, we craft compelling visuals that help businesses grow and connect with their market.",
              icon: "📢"
            }].map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2, duration: 0.6 }}
                className="bg-gray-900/50 p-8 rounded-lg hover:bg-gray-800/50 transition-colors border border-gray-800 hover:scale-105 transform duration-300 ease-in-out shadow-lg hover:shadow-2xl group"
              >
                <div className="text-4xl mb-4">{service.icon}</div>
                <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-burnt-orange transition-colors">
                  {service.title}
                </h3>
                <p className="text-gray-400">{service.description}</p>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/services"
              className="inline-flex items-center text-burnt-orange hover:text-white group transition-colors"
            >
              View All Services
              <svg
                className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section className="py-20 bg-dark-gray/80">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center">
            {/* Image Section */}
            <motion.div
              className="w-full md:w-1/2 mb-10 md:mb-0 md:pr-10"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-burnt-orange to-yellow-500 rounded-2xl opacity-75 blur"></div>
                <motion.img
                  src="/story.jpg"
                  alt="Founder"
                  className="relative rounded-2xl w-full shadow-2xl h-[70vh] transition-transform transform group-hover:scale-105 duration-500 ease-in-out"
                />
              </div>
            </motion.div>

            {/* Text Section */}
            <motion.div
              className="w-full md:w-1/2"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                We Tell Stories That <span className="text-burnt-orange">Matter</span>
              </h2>
              <p className="text-gray-300 mb-6">
                At SAMFREN OJO MEDIA, our work is guided by a set of values that define how we create, collaborate, and show up for every project. These are not just buzzwords they’re principles we live by.
              </p>
              <p className="text-gray-300 mb-8">
                This isn’t just a job for us, it’s what we love. We wake up excited to create, tell stories, and make things that matter. Passion fuels our late nights, drives our problem-solving, and keeps us going even when the work gets tough.
              </p>
              <Link
                to="/about"
                className="inline-flex items-center text-white bg-burnt-orange hover:bg-burnt-orange/90 px-6 py-3 rounded-md font-medium transition-all duration-300 ease-in-out transform hover:scale-105"
              >
                Our Story
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-dark-gray">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">What Our Clients Say</h2>
            <div className="w-20 h-1 bg-burnt-orange mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[{
              quote: "One of the best hands to ever do it. Their services are topnotch, and attention to details is stellar. They come highly recommended any-day or time. 📌📌",
              author: "Olawuyi Philemon Oyewusi",
              initial: "OPO",
            }, {
              quote: "Best cinematographer I have ever worked with. Probably the best in Abuja too. Not just highly skillful but also delivers on time and doesn’t disappoint.",
              author: "Adewale Adeiza",
              initial: "AA",
            }, {
              quote: "Impeccable quality, very detailed and passionate about his work. I worked with Sam and his team earlier in 2025 on a long project and it was seamless. I would recommend him to anyone!",
              author: "Sharon Ogbiti",
              initial: "SO",
            }].map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="bg-gray-900/50 p-8 rounded-lg border border-gray-800 flex flex-col h-full transform transition-transform duration-300 hover:scale-105 hover:shadow-xl hover:bg-gray-800/70"
              >
                <div className="text-yellow-400 text-3xl mb-4">"</div>
                <motion.p
                  className="text-gray-300 italic mb-6 flex-grow"
                  whileHover={{ scale: 1.05 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  {testimonial.quote}
                </motion.p>
                <div className="flex items-center">
                  <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-burnt-orange/30 mr-4">
                    <div className="flex items-center justify-center h-full bg-burnt-orange/20 text-white text-2xl font-semibold">
                      {testimonial.initial}
                    </div>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold">{testimonial.author}</h4>
                    {/* <p className="text-gray-400 text-sm">{testimonial.role}</p> */}
                    <div className="flex mt-1">
                      {[...Array(5)].map((_, i) => (
                        <motion.svg
                          key={i}
                          className="w-5 h-5 text-yellow-400"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.4 + i * 0.1 }}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
                          />
                        </motion.svg>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-dark-gray to-gray-900">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Start Your Project?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Let's collaborate to create something extraordinary. Get in touch with us today to discuss your vision.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              {/* Free Quote Button */}
              <motion.div
                whileHover={{ scale: 1.05, boxShadow: "0px 10px 20px rgba(255, 85, 34, 0.3)" }}
                whileTap={{ scale: 0.98 }}
                transition={{
                  type: "spring",
                  stiffness: 300,
                  damping: 20,
                }}
              >
                <Link
                  to="/contact"
                  className="bg-burnt-orange hover:bg-burnt-orange/90 text-white font-semibold py-3 px-8 rounded-md transition-colors"
                >
                  Get a Free Quote
                </Link>
              </motion.div>

              {/* Contact Us Button */}
              <motion.div
                whileHover={{ scale: 1.05, boxShadow: "0px 10px 20px rgba(255, 255, 255, 0.3)" }}
                whileTap={{ scale: 0.98 }}
                transition={{
                  type: "spring",
                  stiffness: 300,
                  damping: 20,
                }}
              >
                <Link
                  to="/contact"
                  className="bg-transparent hover:bg-white/10 text-white font-semibold py-3 px-8 border-2 border-white rounded-md transition-colors"
                >
                  Contact Us
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
